
import { Module } from './types';
import { StandardQuizQuestion } from '@/types/quizTypes';
import { validateQuizQuality, enhanceQuizQuality } from '@/utils/quizStandardization';

// Safe migration system that converts quickCheck to quiz format with backward compatibility
export const migrateQuickCheckToQuiz = (module: Module): Module => {
  // If module already has quiz, enhance it and keep quickCheck for compatibility
  if (module.quiz) {
    const enhancedQuestions = enhanceQuizQuality(convertToStandardQuestions(module.quiz.questions || []));
    return {
      ...module,
      quiz: {
        ...module.quiz,
        questions: enhancedQuestions.map(q => ({
          id: q.id,
          question: q.question,
          options: q.options,
          correctAnswer: q.correctAnswer,
          correctAnswerIndex: q.correctAnswerIndex,
          difficulty: q.difficulty,
          explanation: q.explanation,
          category: q.category,
          points: q.points,
          type: q.type
        })),
        passThreshold: module.quiz.passThreshold || 75,
        timeLimit: module.quiz.timeLimit || 600,
        category: module.quiz.category || 'react'
      },
      quickCheck: module.quickCheck || {
        questions: enhancedQuestions.map(q => ({
          id: q.id,
          question: q.question,
          options: q.options,
          correctAnswer: q.correctAnswer,
          correctAnswerIndex: q.correctAnswerIndex,
          difficulty: q.difficulty,
          explanation: q.explanation
        })),
        passThreshold: module.quiz.passThreshold || 75
      }
    };
  }

  // If module has quickCheck but no quiz, migrate to quiz format
  if (module.quickCheck) {
    const migratedQuestions = migrateQuickCheckQuestions(module.quickCheck.questions || []);
    const enhancedQuestions = enhanceQuizQuality(migratedQuestions);
    
    return {
      ...module,
      quiz: {
        questions: enhancedQuestions.map(q => ({
          id: q.id,
          question: q.question,
          options: q.options,
          correctAnswer: q.correctAnswer,
          correctAnswerIndex: q.correctAnswerIndex,
          difficulty: q.difficulty,
          explanation: q.explanation,
          category: q.category,
          points: q.points,
          type: q.type
        })),
        passThreshold: module.quickCheck.passThreshold || 75,
        timeLimit: 600,
        category: 'react'
      },
      quickCheck: module.quickCheck // Keep for backward compatibility
    };
  }

  // If module has neither, create basic quiz structure
  return {
    ...module,
    quiz: {
      questions: generateDefaultQuizQuestions(module).map(q => ({
        id: q.id,
        question: q.question,
        options: q.options,
        correctAnswer: q.correctAnswer,
        correctAnswerIndex: q.correctAnswerIndex,
        difficulty: q.difficulty,
        explanation: q.explanation,
        category: q.category,
        points: q.points,
        type: q.type
      })),
      passThreshold: 80,
      timeLimit: 600,
      category: 'react'
    }
  };
};

// Convert existing quiz questions to StandardQuizQuestion format
const convertToStandardQuestions = (questions: any[]): StandardQuizQuestion[] => {
  return questions.map((q, index) => ({
    id: q.id || `converted-q-${index + 1}`,
    question: q.question,
    options: q.options || [],
    correctAnswer: q.correctAnswer || 0,
    correctAnswerIndex: q.correctAnswerIndex !== undefined ? q.correctAnswerIndex : (q.correctAnswer || 0),
    difficulty: q.difficulty || 'medium',
    explanation: q.explanation || generateDefaultExplanation(q.question),
    category: q.category || 'react',
    points: q.points || 15,
    type: q.type || 'multiple-choice'
  }));
};

// Convert quickCheck questions to StandardQuizQuestion format
const migrateQuickCheckQuestions = (questions: any[]): StandardQuizQuestion[] => {
  return questions.map((q, index) => ({
    id: q.id || `migrated-q-${index + 1}`,
    question: q.question,
    options: q.options || [],
    correctAnswer: q.correctAnswerIndex !== undefined ? q.correctAnswerIndex : (q.correctAnswer || 0),
    correctAnswerIndex: q.correctAnswerIndex !== undefined ? q.correctAnswerIndex : (q.correctAnswer || 0),
    difficulty: q.difficulty || 'medium',
    explanation: q.explanation || generateDefaultExplanation(q.question),
    category: q.category || 'react',
    points: q.points || 15,
    type: 'multiple-choice'
  }));
};

// Generate comprehensive explanations for questions that don't have them
const generateDefaultExplanation = (question: string): string => {
  if (question.toLowerCase().includes('react')) {
    return 'This question tests your understanding of React fundamentals and best practices.';
  }
  if (question.toLowerCase().includes('component')) {
    return 'Understanding React components is essential for building reusable and maintainable user interfaces.';
  }
  if (question.toLowerCase().includes('hook')) {
    return 'React Hooks allow you to use state and other React features in functional components.';
  }
  if (question.toLowerCase().includes('prop')) {
    return 'Props are the way data flows from parent to child components in React applications.';
  }
  if (question.toLowerCase().includes('state')) {
    return 'State management is crucial for creating interactive React applications.';
  }
  return 'This concept is fundamental to understanding modern React development practices.';
};

// Generate default quiz questions for modules without any quiz content
const generateDefaultQuizQuestions = (module: Module): StandardQuizQuestion[] => {
  const moduleTitle = module.title.toLowerCase();
  
  const baseQuestions: StandardQuizQuestion[] = [
    {
      id: `${module.id}-default-1`,
      question: `What is the main focus of ${module.title}?`,
      options: [
        'Building interactive user interfaces with React',
        'Only memorizing syntax',
        'Avoiding modern development practices',
        'Ignoring component architecture'
      ],
      correctAnswer: 0,
      correctAnswerIndex: 0,
      difficulty: 'easy',
      explanation: `${module.title} focuses on essential React concepts that help you build modern, interactive user interfaces.`,
      category: 'react',
      points: 10,
      type: 'multiple-choice'
    },
    {
      id: `${module.id}-default-2`,
      question: `Which best practice is emphasized in ${module.title}?`,
      options: [
        'Writing clean, reusable, and maintainable code',
        'Creating complex, hard-to-read solutions',
        'Avoiding component decomposition',
        'Ignoring performance considerations'
      ],
      correctAnswer: 0,
      correctAnswerIndex: 0,
      difficulty: 'medium',
      explanation: `${module.title} emphasizes writing clean, reusable code that follows React best practices and patterns.`,
      category: 'best-practices',
      points: 15,
      type: 'multiple-choice'
    }
  ];

  // Add module-specific questions based on content
  if (moduleTitle.includes('component')) {
    baseQuestions.push({
      id: `${module.id}-component-1`,
      question: 'What makes React components reusable?',
      options: [
        'They can accept different props and maintain their own state',
        'They are written in JavaScript',
        'They use HTML tags',
        'They have CSS styling'
      ],
      correctAnswer: 0,
      correctAnswerIndex: 0,
      difficulty: 'medium',
      explanation: 'React components are reusable because they can accept different props and manage their own state, allowing the same component logic to be used in multiple contexts.',
      category: 'components',
      points: 15,
      type: 'multiple-choice'
    });
  }

  if (moduleTitle.includes('hook')) {
    baseQuestions.push({
      id: `${module.id}-hooks-1`,
      question: 'What are React Hooks?',
      options: [
        'Functions that let you use state and other React features in functional components',
        'CSS styling methods',
        'HTML attributes',
        'JavaScript variables'
      ],
      correctAnswer: 0,
      correctAnswerIndex: 0,
      difficulty: 'medium',
      explanation: 'React Hooks are functions that allow you to "hook into" React features like state and lifecycle methods from functional components.',
      category: 'hooks',
      points: 15,
      type: 'multiple-choice'
    });
  }

  return baseQuestions;
};

// Batch migration for all React course modules
export const migrateAllReactModules = (modules: Module[]): Module[] => {
  return modules.map(migrateQuickCheckToQuiz);
};

// Validation and quality check for migrated modules
export const validateMigratedModule = (module: Module) => {
  if (!module.quiz) {
    return { isValid: false, issues: ['No quiz content after migration'] };
  }

  const standardQuestions = convertToStandardQuestions(module.quiz.questions || []);
  const validation = validateQuizQuality(standardQuestions);
  
  return {
    isValid: validation.isValid,
    issues: validation.issues,
    warnings: validation.warnings,
    score: validation.score,
    questionsCount: module.quiz.questions?.length || 0,
    hasBackwardCompatibility: !!module.quickCheck
  };
};
